import React, { useContext, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  BadgeCheck,
  Loader2,
  QrCode,
  RefreshCw,
  Save,
  Store,
} from "lucide-react";
import ClientSearchSelect from "../common/ClientSearchSelect";
import { GlobalContext } from "../../Context/GlobalContext";
import WhatsappQrGenerateModal from "./ClientConfigTabs/WhatsappQrGenerateModal";
import useSuperAdmin from "../Services/SuperAdminServices/useSuperAdmin";

const STEP_7_ORDER_TYPE_OPTIONS = [
  { value: "home_delivery", label: "Home Delivery" },
  { value: "takeaway", label: "Takeaway" },
  { value: "car_order", label: "Car Order" },
  { value: "dining", label: "Dining" },
];

const STEP_8_ORDER_TYPE_OPTIONS = [
  { value: "homedelivery", label: "Home Delivery" },
  { value: "takeaway", label: "Takeaway" },
  { value: "carorder", label: "Car Order" },
  { value: "dining", label: "Dining" },
];

const DEFAULT_GST_NUMBER = "09AAKCC2923R1Z1";
const DEFAULT_TAX_GST = "5.000000";
const DEFAULT_GST_TYPE = "incl";
const DEFAULT_SUBMERCHANT_ID = "S266823WLBR";

const SUBSCRIPTION_PLANS = [
  {
    pk_plan_id: 1,
    plan_name: "Basic",
    plan_price: 1999,
    plan_duration: "Monthly",
    country_code: "IN",
    currency: "INR",
    is_active: 1,
    sequence: 2,
  },
  {
    pk_plan_id: 2,
    plan_name: "Standard",
    plan_price: 2999,
    plan_duration: "Monthly",
    country_code: "IN",
    currency: "INR",
    is_active: 1,
    sequence: 3,
  },
  {
    pk_plan_id: 3,
    plan_name: "Premium",
    plan_price: 3999,
    plan_duration: "Monthly",
    country_code: "IN",
    currency: "INR",
    is_active: 1,
    sequence: 4,
  },
  {
    pk_plan_id: 4,
    plan_name: "Trial",
    plan_price: 0,
    plan_duration: "For 15 Days",
    country_code: "IN",
    currency: "INR",
    is_active: 0,
    sequence: 1,
  },
  {
    pk_plan_id: 5,
    plan_name: "Basic",
    plan_price: 40,
    plan_duration: "Monthly",
    country_code: "US",
    currency: "USD",
    is_active: 1,
    sequence: 2,
  },
  {
    pk_plan_id: 6,
    plan_name: "Standard",
    plan_price: 75,
    plan_duration: "Monthly",
    country_code: "US",
    currency: "USD",
    is_active: 1,
    sequence: 3,
  },
  {
    pk_plan_id: 7,
    plan_name: "Premium",
    plan_price: 140,
    plan_duration: "Monthly",
    country_code: "US",
    currency: "USD",
    is_active: 1,
    sequence: 4,
  },
  {
    pk_plan_id: 8,
    plan_name: "Trial",
    plan_price: 0,
    plan_duration: "For 15 Days",
    country_code: "US",
    currency: "USD",
    is_active: 0,
    sequence: 1,
  },
];

const ORGANIZATION_TYPE_OPTIONS = [
  {
    value: "1",
    label: "Restaurant",
  },
  {
    value: "2",
    label: "Bakery",
  },
  {
    value: "3",
    label: "Other",
  },
];

const normalizeOrganizationType = (value, fallback = "1") => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();

  if (
    normalizedValue === "1" ||
    normalizedValue === "restaurant" ||
    normalizedValue === "restro"
  ) {
    return "1";
  }

  if (normalizedValue === "2" || normalizedValue === "bakery") {
    return "2";
  }

  if (normalizedValue === "3" || normalizedValue === "other") {
    return "3";
  }

  return fallback;
};

const normalizeYesNo = (value, fallback = "N") => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();
  if (["y", "yes", "1", "true"].includes(normalizedValue)) return "Y";
  if (["n", "no", "0", "false"].includes(normalizedValue)) return "N";
  return fallback;
};

const normalizeChargeType = (value = "") => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase();
  if (["f", "fixed"].includes(normalizedValue)) return "fixed";
  if (["p", "percentage", "percent"].includes(normalizedValue))
    return "percentage";
  return "";
};

const formatOrderType = (value = "") => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase();
  const labels = {
    home_delivery: "Home Delivery",
    homedelivery: "Home Delivery",
    takeaway: "Takeaway",
    takeway: "Takeaway",
    dining: "Dining",
    dinein: "Dining",
    room_order: "Room Order",
    office_order: "Office Order",
    carorder: "Car Order",
    car_order: "Car Order",
  };
  return (
    labels[normalizedValue] ||
    normalizedValue
      .replace(/_/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase()) ||
    "—"
  );
};

const normalizeSingleOrderType = (value = "", apiStep = "7") => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");

  const aliases = {
    home_delivery: "home_delivery",
    homedelivery: "home_delivery",
    takeaway: "takeaway",
    takeway: "takeaway",
    car_order: "car_order",
    carorder: "car_order",
    dining: "dining",
    dinein: "dining",
  };

  const canonicalValue = aliases[normalizedValue] || "";
  if (String(apiStep) === "8") {
    if (canonicalValue === "home_delivery") return "homedelivery";
    if (canonicalValue === "car_order") return "carorder";
  }
  return canonicalValue;
};

const normalizeOrderTypeList = (value, apiStep = "7") => {
  const rawValues = Array.isArray(value)
    ? value
    : String(value || "")
        .split(/[,|;]/)
        .map((item) => item.trim())
        .filter(Boolean);

  return [
    ...new Set(
      rawValues
        .map((item) => normalizeSingleOrderType(item, apiStep))
        .filter(Boolean),
    ),
  ];
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  return value && typeof value === "object" ? [value] : [];
};

const getStep7OrderTypes = (step7) => {
  const rawItems = Array.isArray(step7)
    ? step7
    : step7 !== null && step7 !== undefined
      ? [step7]
      : [];

  const validItems = rawItems
    .flatMap((item) => {
      const orderTypes = normalizeOrderTypeList(
        item && typeof item === "object"
          ? (item?.order_type ?? item?.orderType ?? item?.orderTypes)
          : item,
        "7",
      );

      const status =
        item && typeof item === "object"
          ? normalizeYesNo(item?.status, "N")
          : "Y";

      return orderTypes.map((orderType) => ({
        orderType,
        status,
      }));
    })
    .filter((item) => item.orderType);

  const enabledItems = validItems.filter((item) => item.status === "Y");
  const finalItems = enabledItems.length > 0 ? enabledItems : validItems;

  return [...new Set(finalItems.map((item) => item.orderType))];
};

const getLatestStep8Configs = (step8) => {
  const latestByOrderType = new Map();
  asArray(step8).forEach((item) => {
    const orderType = normalizeSingleOrderType(item?.order_type, "8");
    if (!orderType) return;

    const nextConfig = {
      pkChargeId: item?.pk_charge_id ?? null,
      fromAmount:
        item?.from_amount === null || item?.from_amount === undefined
          ? ""
          : String(item.from_amount),
      toAmount:
        item?.to_amount === null || item?.to_amount === undefined
          ? ""
          : String(item.to_amount),
      charges:
        item?.charges === null || item?.charges === undefined
          ? ""
          : String(item.charges),
      chargesType: normalizeChargeType(item?.charges_type),
      chargesOn:
        item?.charges_on === null || item?.charges_on === undefined
          ? ""
          : String(item.charges_on),
      orderType,
    };

    const previousConfig = latestByOrderType.get(orderType);
    const previousId = Number(previousConfig?.pkChargeId || 0);
    const nextId = Number(nextConfig.pkChargeId || 0);

    if (!previousConfig || nextId >= previousId) {
      latestByOrderType.set(orderType, nextConfig);
    }
  });
  return Array.from(latestByOrderType.values());
};

const createOnboardingInitialForm = () => ({
  editOrganizationInfo: {
    organizationName: "",
    gst: DEFAULT_GST_NUMBER,
    pan_no: "",
    brand_image: "",
    pament_gateway: "EASEBUZZ",
    taxGst: DEFAULT_TAX_GST,
    organizationType: "1",
    gstType: DEFAULT_GST_TYPE,
    status: "Y",
  },

  editReceiptData: {
    address: "",
    phone_no: "",
    tagLine1: "",
    tagLine2: "",
    watermark1: "Order Managed By Chaafo.",
    watermark2: "",
    gst: DEFAULT_GST_NUMBER,
  },

  updateMobileWhatsApp: {
    phone_no: "",
  },

  insertRestaurantOrderDetails: {
    restaurantgst: DEFAULT_GST_NUMBER,
    restaurantName: "",
    takeaway: "N",
    homeDelivery: "Y",
    restaurantAddress: "",
    restaurantPincode: "",
    dining: "N",
    carOrder: "N",
  },

  createOrUpdateSubmerchant: {
    submerchant_id: DEFAULT_SUBMERCHANT_ID,
    status: "Y",
  },

  updateDeliveryCharges: {
    charges: "",
    fromAmount: "",
    toAmount: "",
    chargeType: "",
  },

  updateOrderTypeOptions: {
    orderType: ["home_delivery"],
  },

  insertOrderChargesConfig: {
    activeOrderType: "homedelivery",
    configs: [
      {
        pkChargeId: null,
        fromAmount: "",
        toAmount: "",
        charges: "",
        chargesType: "",
        chargesOn: "order",
        orderType: "homedelivery",
      },
    ],
  },

  changeSubscriptionPlanV2: {
    // Step 9 current subscription is refreshed from getSubscriptionPlan.
    subscriptionId: "",
    currentPlanId: "",
    currentPlanName: "",
    currentPlanPrice: "",
    currentPlanCurrency: "",
    currentPlanDuration: "",
    currentRemainingDays: "",
    billingCycle: "",
    startDate: "",
    endDate: "",
    purchasedOn: "",
    expiredOn: "",
    subscriptionType: "",
    isTrial: "",
    remark: "",
    paymentStatus: "",

    // Step 9 update: planId OR days. Never send both.
    planId: "",
    days: "",
  },
});

const onboardingSections = [
  {
    title: "Organization Info",
    section: "editOrganizationInfo",
    apiStep: "1",
    fields: [
      {
        name: "brand_image",
        label: "Brand Image",
        type: "image",
      },
      {
        name: "organizationName",
        label: "Organization Name",
        type: "clientSelect",
        required: true,
      },
      {
        name: "status",
        label: "Enable GST",
        type: "ynToggle",
      },
      {
        name: "gst",
        label: "GST",
      },
      {
        name: "pan_no",
        label: "PAN No",
      },
      {
        name: "organizationType",
        label: "Organization Type",
        type: "organizationTypeSelect",
      },
      {
        name: "pament_gateway",
        label: "Payment Gateway",
        type: "gateway",
      },
      {
        name: "taxGst",
        label: "Tax GST",
      },
      {
        name: "gstType",
        label: "GST Type",
        type: "gstTypeSelect",
      },
    ],
  },
  {
    title: "Receipt Data",
    section: "editReceiptData",
    apiStep: "2",
    fields: [
      { name: "address", label: "Receipt Address" },
      { name: "phone_no", label: "Receipt Phone No" },
      { name: "tagLine1", label: "Tag Line 1" },
      { name: "tagLine2", label: "Tag Line 2" },
      { name: "watermark1", label: "Watermark 1", type: "watermarkSelect" },
      { name: "watermark2", label: "Watermark 2" },
      { name: "gst", label: "Receipt GST" },
    ],
  },
  {
    title: "Mobile / WhatsApp",
    section: "updateMobileWhatsApp",
    apiStep: "3",
    fields: [{ name: "phone_no", label: "WhatsApp Phone No" }],
  },
  {
    title: "Restaurant Order Details",
    section: "insertRestaurantOrderDetails",
    apiStep: "4",
    fields: [
      { name: "restaurantgst", label: "Restaurant GST" },
      { name: "restaurantName", label: "Restaurant Name", required: true },
      { name: "restaurantAddress", label: "Restaurant Address" },
      { name: "restaurantPincode", label: "Restaurant Pincode" },
      {
        name: "orderModes",
        label: "Order Modes",
        type: "ynToggleGroup",
        options: [
          { name: "homeDelivery", label: "Home Delivery" },
          { name: "dining", label: "Dining" },
          { name: "carOrder", label: "Car Order" },
          { name: "takeaway", label: "Takeaway" },
        ],
      },
    ],
  },
  {
    title: "Submerchant",
    section: "createOrUpdateSubmerchant",
    apiStep: "5",
    fields: [
      {
        name: "status",
        label: "Enable Easebuzz Submerchant",
        type: "ynToggle",
      },
      {
        name: "submerchant_id",
        label: "Submerchant ID",
        required: true,
      },
    ],
  },
  {
    title: "Delivery Charges",
    section: "updateDeliveryCharges",
    apiStep: "6",
    fields: [
      { name: "charges", label: "Charges" },
      { name: "fromAmount", label: "From Amount" },
      { name: "toAmount", label: "To Amount" },
      { name: "chargeType", label: "Charge Type", type: "chargeTypeSelect" },
    ],
  },
  {
    title: "Pos Order Type Options",
    section: "updateOrderTypeOptions",
    apiStep: "7",
    fields: [
      {
        name: "orderType",
        label: "Order Type Options",
        type: "orderTypeStep7",
      },
    ],
  },
  {
    title: "Order Charges Config",
    section: "insertOrderChargesConfig",
    apiStep: "8",
    fields: [
      {
        name: "activeOrderType",
        label: "Select Order Type to Configure",
        type: "orderTypeStep8",
      },
      { name: "fromAmount", label: "From Amount", type: "step8ConfigField" },
      { name: "toAmount", label: "To Amount", type: "step8ConfigField" },
      { name: "charges", label: "Charges", type: "step8ConfigField" },
      {
        name: "chargesType",
        label: "Charges Type",
        type: "step8ChargeTypeSelect",
      },
      { name: "chargesOn", label: "Charges On", type: "step8ChargesOnSelect" },
    ],
  },
  {
    title: "Subscription Plan",
    section: "changeSubscriptionPlanV2",
    apiStep: "9",
    fields: [],
  },
];

export default function OnboardingRestaurantPanel({
  clients = [],
  gateways = [],
  onboardRestaurantStep,
  getOnboardedRestaurantDetails,
  mode,
}) {
  const { anlookApi, adminId: contextAdminId } = useContext(GlobalContext);
  const { getSubscriptionPlan, changeSubscriptionPlan } = useSuperAdmin();
  const [form, setForm] = useState(createOnboardingInitialForm);
  const [saving, setSaving] = useState(false);
  const [checkingRestaurant, setCheckingRestaurant] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientCode, setSelectedClientCode] = useState("");
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [finishModalOpen, setFinishModalOpen] = useState(false);
  const [finishResponseRow, setFinishResponseRow] = useState(null);
  const [currentSubscriptionPlan, setCurrentSubscriptionPlan] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionChanging, setSubscriptionChanging] = useState(false);
  const [finalizingOnboarding, setFinalizingOnboarding] = useState(false);
  const [selectedSubscriptionPlanId, setSelectedSubscriptionPlanId] =
    useState("");

  // GST ON values are preserved separately.
  // OFF mode can be edited freely, but turning GST back ON restores these values.
  const gstEnabledSnapshotRef = useRef({
    gst: DEFAULT_GST_NUMBER,
    taxGst: DEFAULT_TAX_GST,
    gstType: DEFAULT_GST_TYPE,
    receiptGst: DEFAULT_GST_NUMBER,
    restaurantGst: DEFAULT_GST_NUMBER,
  });

  // Exact same state and logic for opening WhatsappQrGenerateModal as ClientDetailsTab
  const [showWhatsappQrModal, setShowWhatsappQrModal] = useState(false);

  const currentSection = onboardingSections[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === onboardingSections.length - 1;

  const getClientCode = (client) => {
    if (typeof client === "string" || typeof client === "number") {
      return String(client).trim();
    }
    return String(
      client?.us_code || client?.code || client?.value || client?.id || "",
    ).trim();
  };

  const getClientName = (client) => {
    if (!client || typeof client !== "object") return "";
    return String(
      client?.organization_name ||
        client?.us_name ||
        client?.name ||
        client?.label ||
        client?.title ||
        "",
    ).trim();
  };

  const isGatewayActive = (gate) =>
    gate?.is_active === true ||
    gate?.is_active === "true" ||
    gate?.is_active === "Y" ||
    gate?.is_active === "y" ||
    gate?.is_active === 1 ||
    gate?.is_active === "1";

  const getAdminPkId = () => {
    try {
      return JSON.parse(localStorage.getItem("Admin") || "{}")?.pkid || "3";
    } catch {
      return "3";
    }
  };

  const getSubscriptionCountry = () =>
    String(
      selectedClient?.country_code ||
        selectedClient?.countryCode ||
        selectedClient?.country ||
        "IN",
    )
      .trim()
      .toUpperCase() || "IN";

  const getCurrentSubscriptionPlanId = (plan = currentSubscriptionPlan) =>
    String(
      plan?.fk_plan_id ??
        plan?.plan_id ??
        plan?.pk_plan_id ??
        plan?.planId ??
        "",
    ).trim();

  const syncSubscriptionToForm = (plan) => {
    if (!plan || typeof plan !== "object") return;

    setForm((prev) => ({
      ...prev,
      changeSubscriptionPlanV2: {
        ...prev.changeSubscriptionPlanV2,
        subscriptionId: String(
          plan?.id ?? plan?.subscription_id ?? plan?.pk_subscription_id ?? "",
        ),
        currentPlanId: getCurrentSubscriptionPlanId(plan),
        currentPlanName: String(plan?.plan_name ?? ""),
        currentPlanPrice: String(plan?.plan_price ?? ""),
        currentPlanCurrency: String(plan?.currency ?? ""),
        currentPlanDuration: String(
          plan?.plan_duration ?? plan?.billing_cycle ?? "",
        ),
        currentRemainingDays: String(plan?.remaining_days ?? ""),
        billingCycle: String(plan?.billing_cycle ?? ""),
        startDate: String(plan?.start_date ?? ""),
        endDate: String(plan?.end_date ?? ""),
        purchasedOn: String(plan?.purchased_on ?? ""),
        expiredOn: String(plan?.expired_on ?? plan?.end_date ?? ""),
        subscriptionType: String(plan?.subscription_type ?? ""),
        isTrial: String(plan?.is_trial ?? ""),
        remark: String(plan?.remark ?? ""),
        paymentStatus: String(plan?.payment_status ?? ""),
      },
    }));
  };

  const loadCurrentSubscription = async (userid = selectedClientCode) => {
    const normalizedUserId = String(userid || "").trim();
    if (!normalizedUserId || !getSubscriptionPlan) return null;

    try {
      setSubscriptionLoading(true);

      const result = await getSubscriptionPlan({
        adminId: String(contextAdminId || getAdminPkId()),
        userid: normalizedUserId,
      });

      if (result?.success && result?.data) {
        setCurrentSubscriptionPlan(result.data);
        syncSubscriptionToForm(result.data);
        setSelectedSubscriptionPlanId("");
        return result.data;
      }

      setCurrentSubscriptionPlan(null);
      return null;
    } catch (error) {
      console.error("getSubscriptionPlan error:", error);
      setCurrentSubscriptionPlan(null);
      return null;
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const normalizeValue = (value, fallback = "") =>
    value === null || value === undefined ? fallback : String(value);

  const getFirstNonEmptyValue = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;

      const stringValue = String(value);
      if (stringValue.trim() !== "") {
        return stringValue;
      }
    }

    return "";
  };

  const rememberGstEnabledValues = (formState = {}) => {
    const organizationInfo = formState?.editOrganizationInfo || {};
    const receiptData = formState?.editReceiptData || {};
    const restaurantDetails = formState?.insertRestaurantOrderDetails || {};

    const gst = getFirstNonEmptyValue(
      organizationInfo?.gst,
      DEFAULT_GST_NUMBER,
    );

    gstEnabledSnapshotRef.current = {
      gst,
      taxGst: getFirstNonEmptyValue(organizationInfo?.taxGst, DEFAULT_TAX_GST),
      gstType: getFirstNonEmptyValue(
        organizationInfo?.gstType,
        DEFAULT_GST_TYPE,
      ),
      receiptGst: getFirstNonEmptyValue(receiptData?.gst, gst),
      restaurantGst: getFirstNonEmptyValue(
        restaurantDetails?.restaurantgst,
        gst,
      ),
    };
  };

  const extractOnboardingRows = (result) => {
    const possibleResponses = [
      result?.response,
      result?.data?.response,
      result?.data?.data?.response,
      result?.result?.response,
      result?.response?.response,
    ];
    return possibleResponses.find(Array.isArray) || [];
  };

  const normalizeIndianMobileNumber = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (digits.startsWith("91") && digits.length === 12) {
      return digits.slice(2);
    }
    return digits;
  };

  const isValidIndianMobileNumber = (value) => {
    const mobileNumber = normalizeIndianMobileNumber(value);
    return /^[6-9]\d{9}$/.test(mobileNumber);
  };

  const createNewRestaurantPrefilledForm = (client = null) => {
    const initialForm = createOnboardingInitialForm();

    if (!client || typeof client !== "object") {
      return initialForm;
    }

    const clientName = getClientName(client);
    const clientGst = getFirstNonEmptyValue(
      client?.gst,
      client?.gst_number,
      client?.gst_no,
      client?.restaurant_gst,
      initialForm.editOrganizationInfo.gst,
    );
    const clientPan = getFirstNonEmptyValue(
      client?.pan_no,
      client?.pan,
      client?.pan_number,
    );
    const clientBrandImage = getFirstNonEmptyValue(
      client?.brand_image,
      client?.organization_image,
      client?.logo,
      client?.image,
    );
    const clientAddress = getFirstNonEmptyValue(
      client?.address,
      client?.restaurant_address,
      client?.organization_address,
      client?.us_address,
      client?.full_address,
    );
    const clientPincode = getFirstNonEmptyValue(
      client?.pincode,
      client?.pin_code,
      client?.postal_code,
      client?.restaurant_pincode,
    );
    const clientPhone = normalizeIndianMobileNumber(
      getFirstNonEmptyValue(
        client?.phone_no,
        client?.mobile,
        client?.mobile_no,
        client?.phone,
        client?.us_loginid,
        client?.login_id,
      ),
    );
    const paymentGateway = getFirstNonEmptyValue(
      client?.payment_gateway,
      client?.pament_gateway,
      initialForm.editOrganizationInfo.pament_gateway,
    );
    const taxGst = getFirstNonEmptyValue(
      client?.tax_gst,
      client?.taxGst,
      initialForm.editOrganizationInfo.taxGst,
    );
    const gstType = getFirstNonEmptyValue(
      client?.gst_type,
      client?.gstType,
      initialForm.editOrganizationInfo.gstType,
    );
    const organizationType = normalizeOrganizationType(
      client?.org_type ?? client?.organization_type ?? client?.organizationType,
      initialForm.editOrganizationInfo.organizationType,
    );

    return {
      ...initialForm,
      editOrganizationInfo: {
        ...initialForm.editOrganizationInfo,
        organizationName: clientName,
        gst: clientGst,
        pan_no: clientPan,
        brand_image: clientBrandImage,
        pament_gateway: paymentGateway,
        taxGst,
        organizationType,
        gstType,
      },
      editReceiptData: {
        ...initialForm.editReceiptData,
        address: clientAddress,
        phone_no: clientPhone,
        gst: clientGst,
      },
      updateMobileWhatsApp: {
        ...initialForm.updateMobileWhatsApp,
        phone_no: clientPhone,
      },
      insertRestaurantOrderDetails: {
        ...initialForm.insertRestaurantOrderDetails,
        restaurantgst: clientGst,
        restaurantName: clientName,
        restaurantAddress: clientAddress,
        restaurantPincode: clientPincode,
      },
    };
  };

  const mapGetResponseToForm = (row, fallbackName = "") => {
    const step6Item = asArray(row?.step6)[0] || {};
    const step7OrderTypes = getStep7OrderTypes(row?.step7);

    const step8Configs = getLatestStep8Configs(row?.step8);
    const firstStep8OrderType = step8Configs[0]?.orderType || "homedelivery";
    const step9Item = asArray(row?.step9)[0] || {};

    const organizationName = getFirstNonEmptyValue(
      row?.step1?.organization_name,
      fallbackName,
    );

    const organizationGst = getFirstNonEmptyValue(row?.step1?.gst);

    const receiptAddress = getFirstNonEmptyValue(row?.step2?.address);
    const receiptPhone = getFirstNonEmptyValue(row?.step2?.phone_no);

    const whatsappPhone = normalizeIndianMobileNumber(
      getFirstNonEmptyValue(row?.step3?.phone_no, receiptPhone),
    );

    return {
      editOrganizationInfo: {
        organizationName,

        gst: organizationGst,

        pan_no: normalizeValue(row?.step1?.pan_no),

        brand_image: normalizeValue(row?.step1?.brand_image),

        pament_gateway: normalizeValue(row?.step1?.payment_gateway, "EASEBUZZ"),

        taxGst: normalizeValue(row?.step1?.tax_gst),

        organizationType: normalizeOrganizationType(row?.step1?.org_type, "1"),

        gstType: normalizeValue(row?.step1?.gst_type),

        status: normalizeYesNo(row?.step1?.status, row?.step1?.gst ? "Y" : "N"),
      },
      editReceiptData: {
        address: receiptAddress,
        phone_no: receiptPhone,
        tagLine1: normalizeValue(row?.step2?.tag_line1),
        tagLine2: normalizeValue(row?.step2?.tag_line2),
        watermark1: normalizeValue(
          row?.step2?.water_mark1,
          "Order Managed By Chaafo.",
        ),
        watermark2: normalizeValue(row?.step2?.water_mark2),
        gst: getFirstNonEmptyValue(row?.step2?.gst, organizationGst),
      },
      updateMobileWhatsApp: {
        phone_no: whatsappPhone,
      },
      insertRestaurantOrderDetails: {
        restaurantgst: getFirstNonEmptyValue(
          row?.step4?.restaurant_gst,
          organizationGst,
        ),
        restaurantName: getFirstNonEmptyValue(
          row?.step4?.restaurant_name,
          organizationName,
          fallbackName,
        ),
        takeaway: normalizeYesNo(row?.step4?.takeaway, "N"),
        homeDelivery: normalizeYesNo(row?.step4?.home_delivery, "Y"),
        restaurantAddress: getFirstNonEmptyValue(
          row?.step4?.restaurant_address,
          receiptAddress,
        ),
        restaurantPincode: normalizeValue(row?.step4?.restaurant_pincode),
        dining: normalizeYesNo(row?.step4?.dining, "N"),
        carOrder: normalizeYesNo(row?.step4?.car_order, "N"),
      },
      createOrUpdateSubmerchant: (() => {
        const hasSavedStep5 =
          row?.step5 &&
          typeof row.step5 === "object" &&
          Object.keys(row.step5).length > 0;

        // Existing saved Step 5 hai to API value respect karenge.
        // Step 5 missing/new hai to default ON rahega.
        const submerchantStatus = hasSavedStep5
          ? normalizeYesNo(row?.step5?.status, "Y")
          : "Y";

        const savedSubmerchantId = normalizeValue(
          row?.step5?.submerchant_id,
        ).trim();

        return {
          status: submerchantStatus,

          // API me ID hai to wahi.
          // Missing hai to default Easebuzz submerchant ID.
          submerchant_id: savedSubmerchantId || DEFAULT_SUBMERCHANT_ID,
        };
      })(),
      updateDeliveryCharges: {
        charges: normalizeValue(step6Item?.charges),
        fromAmount: normalizeValue(step6Item?.from_amount),
        toAmount: normalizeValue(step6Item?.to_amount),
        chargeType: normalizeChargeType(step6Item?.charge_type),
      },
      updateOrderTypeOptions: {
        orderType:
          step7OrderTypes.length > 0 ? step7OrderTypes : ["home_delivery"],
      },
      insertOrderChargesConfig: {
        activeOrderType: firstStep8OrderType,
        configs:
          step8Configs.length > 0
            ? step8Configs
            : [
                {
                  pkChargeId: null,
                  fromAmount: "",
                  toAmount: "",
                  charges: "",
                  chargesType: "",
                  chargesOn: "order",
                  orderType: "homedelivery",
                },
              ],
      },
      changeSubscriptionPlanV2: {
        // Onboarding step9 is kept as initial/fallback display data.
        // Dedicated getSubscriptionPlan refreshes this when the client is selected.
        subscriptionId: normalizeValue(step9Item?.id),
        currentPlanId: normalizeValue(
          step9Item?.fk_plan_id ?? step9Item?.plan_id ?? step9Item?.pk_plan_id,
        ),
        currentPlanName: normalizeValue(step9Item?.plan_name),
        currentPlanPrice: normalizeValue(step9Item?.plan_price),
        currentPlanCurrency: normalizeValue(step9Item?.currency),
        currentPlanDuration: normalizeValue(step9Item?.plan_duration),
        currentRemainingDays: normalizeValue(step9Item?.remaining_days),
        billingCycle: normalizeValue(step9Item?.billing_cycle),
        startDate: normalizeValue(step9Item?.start_date),
        endDate: normalizeValue(step9Item?.end_date),
        purchasedOn: normalizeValue(step9Item?.purchased_on),
        expiredOn: normalizeValue(step9Item?.expired_on),
        subscriptionType: normalizeValue(step9Item?.subscription_type),
        isTrial: normalizeValue(step9Item?.is_trial),
        remark: normalizeValue(step9Item?.remark),
        paymentStatus: normalizeValue(step9Item?.payment_status),
        planId: "",
        days: "",
      },
    };
  };

  const createCompletedSteps = (savedStep) => {
    const stepNumber = Number(savedStep || 0);
    return onboardingSections.reduce((acc, section, index) => {
      if (index + 1 <= stepNumber) {
        acc[section.section] = true;
      }
      return acc;
    }, {});
  };

  const copyIfNotCustomized = (
    targetValue,
    previousSourceValue,
    newSourceValue,
  ) => {
    const target = String(targetValue ?? "").trim();
    const previousSource = String(previousSourceValue ?? "").trim();

    // Target empty hai ya abhi source ke same tha,
    // tabhi new value automatically copy hogi.
    //
    // Agar user target field manually change kar chuka hai,
    // uski value overwrite nahi hogi.
    if (!target || target === previousSource) {
      return newSourceValue;
    }

    return targetValue;
  };

  const updateField = (section, name, value) => {
    setForm((prev) => {
      const nextForm = {
        ...prev,

        [section]: {
          ...prev[section],
          [name]: value,
        },
      };

      // ==========================================
      // STEP 1 GST STATUS
      // Default ON. ON values are preserved separately.
      // OFF keeps the current GST values visible but unlocks them for editing.
      // Turning ON again restores the preserved ON values, not the values
      // entered while GST was OFF.
      // ==========================================
      if (section === "editOrganizationInfo" && name === "status") {
        const nextStatus = normalizeYesNo(value, "Y");

        if (nextStatus === "N") {
          // Preserve current ON values, but DO NOT clear the visible fields.
          // OFF only changes the mode from locked -> editable.
          rememberGstEnabledValues(prev);

          nextForm.editOrganizationInfo = {
            ...prev.editOrganizationInfo,
            status: "N",
          };
        } else {
          const savedGstValues = gstEnabledSnapshotRef.current || {};
          const restoredGst = savedGstValues.gst || DEFAULT_GST_NUMBER;

          nextForm.editOrganizationInfo = {
            ...prev.editOrganizationInfo,
            status: "Y",
            gst: restoredGst,
            taxGst: savedGstValues.taxGst || DEFAULT_TAX_GST,
            gstType: savedGstValues.gstType || DEFAULT_GST_TYPE,
          };

          nextForm.editReceiptData = {
            ...prev.editReceiptData,
            gst: savedGstValues.receiptGst || restoredGst,
          };

          nextForm.insertRestaurantOrderDetails = {
            ...prev.insertRestaurantOrderDetails,
            restaurantgst: savedGstValues.restaurantGst || restoredGst,
          };
        }
      }

      // ==========================================
      // STEP 1 GST
      // GST -> Receipt GST + Restaurant GST
      // ==========================================
      if (section === "editOrganizationInfo" && name === "gst") {
        nextForm.editReceiptData = {
          ...prev.editReceiptData,

          gst: copyIfNotCustomized(
            prev.editReceiptData?.gst,
            prev.editOrganizationInfo?.gst,
            value,
          ),
        };

        nextForm.insertRestaurantOrderDetails = {
          ...prev.insertRestaurantOrderDetails,

          restaurantgst: copyIfNotCustomized(
            prev.insertRestaurantOrderDetails?.restaurantgst,
            prev.editOrganizationInfo?.gst,
            value,
          ),
        };
      }

      // ==========================================
      // STEP 1 ORGANIZATION NAME
      // Organization Name -> Restaurant Name
      // ==========================================
      if (section === "editOrganizationInfo" && name === "organizationName") {
        nextForm.insertRestaurantOrderDetails = {
          ...nextForm.insertRestaurantOrderDetails,

          restaurantName: copyIfNotCustomized(
            prev.insertRestaurantOrderDetails?.restaurantName,
            prev.editOrganizationInfo?.organizationName,
            value,
          ),
        };
      }

      // ==========================================
      // STEP 2 PHONE
      // Receipt Phone -> WhatsApp Phone
      // ==========================================
      if (section === "editReceiptData" && name === "phone_no") {
        nextForm.updateMobileWhatsApp = {
          ...prev.updateMobileWhatsApp,

          phone_no: copyIfNotCustomized(
            prev.updateMobileWhatsApp?.phone_no,
            prev.editReceiptData?.phone_no,
            value,
          ),
        };
      }

      // ==========================================
      // STEP 2 ADDRESS
      // Receipt Address -> Restaurant Address
      // ==========================================
      if (section === "editReceiptData" && name === "address") {
        nextForm.insertRestaurantOrderDetails = {
          ...nextForm.insertRestaurantOrderDetails,

          restaurantAddress: copyIfNotCustomized(
            prev.insertRestaurantOrderDetails?.restaurantAddress,
            prev.editReceiptData?.address,
            value,
          ),
        };
      }

      // ==========================================
      // STEP 5 SUBMERCHANT
      // Default ON + auto fill submerchant ID
      // ==========================================
      if (section === "createOrUpdateSubmerchant" && name === "status") {
        const nextStatus = normalizeYesNo(value, "Y");

        nextForm.createOrUpdateSubmerchant = {
          ...prev.createOrUpdateSubmerchant,

          status: nextStatus,

          submerchant_id:
            nextStatus === "Y"
              ? String(
                  prev.createOrUpdateSubmerchant?.submerchant_id ||
                    DEFAULT_SUBMERCHANT_ID,
                ).trim()
              : prev.createOrUpdateSubmerchant?.submerchant_id || "",
        };
      }

      return nextForm;
    });
  };

  const toggleOrderType = (section, name, value, apiStep) => {
    setForm((prev) => {
      const selectedValues = normalizeOrderTypeList(
        prev?.[section]?.[name],
        apiStep,
      );
      const normalizedValue = normalizeSingleOrderType(value, apiStep);
      const alreadySelected = selectedValues.includes(normalizedValue);

      return {
        ...prev,
        [section]: {
          ...prev[section],
          [name]: alreadySelected
            ? selectedValues.filter((item) => item !== normalizedValue)
            : [...selectedValues, normalizedValue],
        },
      };
    });
  };

  const selectStep8OrderType = (value) => {
    const normalizedOrderType = normalizeSingleOrderType(value, "8");
    if (!normalizedOrderType) return;

    setForm((prev) => {
      const currentSectionState = prev.insertOrderChargesConfig || {};
      const currentConfigs = Array.isArray(currentSectionState.configs)
        ? currentSectionState.configs
        : [];

      const alreadyExists = currentConfigs.some(
        (item) => item.orderType === normalizedOrderType,
      );

      return {
        ...prev,
        insertOrderChargesConfig: {
          activeOrderType: normalizedOrderType,
          configs: alreadyExists
            ? currentConfigs
            : [
                ...currentConfigs,
                {
                  pkChargeId: null,
                  fromAmount: "",
                  toAmount: "",
                  charges: "",
                  chargesType: "",
                  chargesOn: "order",
                  orderType: normalizedOrderType,
                },
              ],
        },
      };
    });
  };

  const updateStep8ConfigField = (name, value) => {
    setForm((prev) => {
      const currentSectionState = prev.insertOrderChargesConfig || {};
      const activeOrderType = currentSectionState.activeOrderType || "";
      const currentConfigs = Array.isArray(currentSectionState.configs)
        ? currentSectionState.configs
        : [];

      if (!activeOrderType) return prev;

      return {
        ...prev,
        insertOrderChargesConfig: {
          ...currentSectionState,
          configs: currentConfigs.map((item) =>
            item.orderType === activeOrderType
              ? {
                  ...item,
                  [name]: value,
                }
              : item,
          ),
        },
      };
    });
  };

  const resetFormState = (client = null) => {
    const initialForm = createNewRestaurantPrefilledForm(client);

    rememberGstEnabledValues(initialForm);
    setForm(initialForm);

    setImagePreview(initialForm.editOrganizationInfo.brand_image || "");
    setCurrentStepIndex(0);
    setCompletedSteps({});
    setIsOnboarded(false);
    setFinishModalOpen(false);
    setFinishResponseRow(null);
    setCurrentSubscriptionPlan(null);
    setSelectedSubscriptionPlanId("");
    setSubscriptionLoading(false);
    setSubscriptionChanging(false);
    setShowWhatsappQrModal(false);
  };

  const fetchAndFillRestaurant = async (client) => {
    const userid = getClientCode(client);
    const clientName = getClientName(client);

    if (!userid) {
      resetFormState(client);
      return;
    }

    if (!getOnboardedRestaurantDetails) {
      toast.error("Get onboarded restaurant API function missing");
      resetFormState(client);
      return;
    }

    try {
      setCheckingRestaurant(true);
      const res = await getOnboardedRestaurantDetails({ userId: userid });

      const responseCode =
        res?.responseCode ??
        res?.data?.responseCode ??
        res?.data?.data?.responseCode ??
        res?.result?.responseCode;

      const rows = responseCode === "00" ? extractOnboardingRows(res) : [];

      const matchedRow =
        rows.find((row) => String(row?.userId || "").trim() === userid) ||
        rows[0] ||
        null;

      if (matchedRow) {
        const savedStep = Number(matchedRow?.step || 0);

        // Some new restaurants return an empty/step-0 row from the GET API.
        // Treat that as NEW onboarding, otherwise blank API values overwrite
        // the defaults/prefilled client data.
        if (savedStep <= 0) {
          resetFormState(client);
          await loadCurrentSubscription(userid);
          return matchedRow;
        }

        const mappedForm = mapGetResponseToForm(matchedRow, clientName);

        // Existing ON GST values become the restore snapshot. If the saved
        // restaurant currently has GST OFF, the helper safely falls back to
        // the standard ON defaults for a future ON toggle.
        rememberGstEnabledValues(mappedForm);
        setForm(mappedForm);
        setImagePreview(mappedForm.editOrganizationInfo.brand_image || "");
        setCompletedSteps(createCompletedSteps(savedStep));
        setCurrentStepIndex(
          Math.min(Math.max(savedStep, 0), onboardingSections.length - 1),
        );
        setIsOnboarded(true);
        setFinishResponseRow(matchedRow);
        await loadCurrentSubscription(userid);
        return matchedRow;
      }

      resetFormState(client);
      await loadCurrentSubscription(userid);
      return null;
    } catch (error) {
      console.error("getOnboardedRestaurantDetails error:", error);
      resetFormState(client);
      toast.error("Could not check onboarding details");
      return null;
    } finally {
      setCheckingRestaurant(false);
    }
  };

  const handleOrganizationSelect = async (clientOrCode) => {
    const incomingCode = getClientCode(clientOrCode);

    const resolvedClient =
      clientOrCode && typeof clientOrCode === "object"
        ? clientOrCode
        : (clients || []).find(
            (item) => getClientCode(item) === incomingCode,
          ) || null;

    const clientCode = incomingCode || getClientCode(resolvedClient);

    setSelectedClient(resolvedClient);
    setSelectedClientCode(clientCode);

    if (!clientCode) {
      resetFormState(null);
      return;
    }

    await fetchAndFillRestaurant(resolvedClient || clientCode);
  };

  const handleReset = () => {
    setSelectedClient(null);
    setSelectedClientCode("");
    resetFormState(null);
  };

  const uploadBrandImage = async (file) => {
    if (!file) {
      throw new Error("Image file missing");
    }

    if (!anlookApi) {
      throw new Error("Anlook API instance missing");
    }

    const maxSizeBytes = 2 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      throw new Error("Image must be under 2MB");
    }

    const uploadUserid =
      selectedClientCode ||
      getClientCode(selectedClient) ||
      "onboarding_restaurant";

    const imageData = await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Could not read image file"));
      reader.readAsDataURL(file);
    });

    const response = await anlookApi.post("/v1/uploadItemImagev2.php", {
      userid: String(uploadUserid),
      item_name: "organization_image",
      type: "IMG",
      image_data: imageData,
    });

    const responseData = response?.data || {};

    if (String(responseData?.status) !== "0" || !responseData?.image_url) {
      throw new Error(
        responseData?.message ||
          responseData?.responseMessage ||
          "Organization image upload failed",
      );
    }

    return responseData.image_url;
  };

  const handleBrandImageSelect = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      setUploadingImage(true);

      const localPreview = URL.createObjectURL(file);

      setImagePreview(localPreview);

      const uploadedUrl = await uploadBrandImage(file);

      if (!uploadedUrl) {
        throw new Error("Image URL missing");
      }

      updateField("editOrganizationInfo", "brand_image", uploadedUrl);

      setImagePreview(uploadedUrl);

      toast.success("Brand image uploaded successfully");
    } catch (error) {
      console.error("Brand image upload failed:", error);

      toast.error(error?.message || "Failed to upload brand image");
    } finally {
      setUploadingImage(false);

      event.target.value = "";
    }
  };

  const validateCurrentStep = () => {
    if (!selectedClientCode) {
      toast.error("Please select organization first");
      return false;
    }

    if (currentSection.section === "editOrganizationInfo") {
      const organizationInfo = form.editOrganizationInfo;

      if (!organizationInfo.organizationName?.trim()) {
        toast.error("Organization name is required");
        return false;
      }

      if (!organizationInfo.organizationType) {
        toast.error("Please select organization type");
        return false;
      }

      if (normalizeYesNo(organizationInfo.status, "N") === "Y") {
        if (!organizationInfo.gst?.trim()) {
          toast.error("GST is required when GST is enabled");
          return false;
        }

        if (!organizationInfo.taxGst?.trim()) {
          toast.error("Tax GST is required when GST is enabled");
          return false;
        }

        if (!organizationInfo.gstType?.trim()) {
          toast.error("GST type is required when GST is enabled");
          return false;
        }
      }
    }

    if (currentSection.section === "updateMobileWhatsApp") {
      const phoneNumber = normalizeIndianMobileNumber(
        form.updateMobileWhatsApp.phone_no,
      );

      if (!phoneNumber) {
        toast.error("WhatsApp phone number is required");
        return false;
      }

      if (!isValidIndianMobileNumber(phoneNumber)) {
        toast.error(
          "Enter a valid 10-digit WhatsApp number starting with 6, 7, 8 or 9",
        );
        return false;
      }
    }

    if (
      currentSection.section === "insertRestaurantOrderDetails" &&
      !form.insertRestaurantOrderDetails.restaurantName?.trim()
    ) {
      toast.error("Restaurant name is required");
      return false;
    }

    if (currentSection.section === "createOrUpdateSubmerchant") {
      const submerchantStatus = normalizeYesNo(
        form.createOrUpdateSubmerchant.status,
        "N",
      );

      const submerchantId =
        form.createOrUpdateSubmerchant.submerchant_id?.trim();

      if (!submerchantId) {
        toast.error(
          submerchantStatus === "Y"
            ? "Easebuzz Submerchant ID is required"
            : "Please enter custom Submerchant ID",
        );
        return false;
      }
    }

    if (
      currentSection.section === "insertOrderChargesConfig" &&
      !form.insertOrderChargesConfig?.activeOrderType
    ) {
      toast.error("Please select an order type");
      return false;
    }

    if (currentSection.section === "changeSubscriptionPlanV2") {
      const hasExistingSubscription = Boolean(
        currentSubscriptionPlan ||
        form.changeSubscriptionPlanV2?.currentPlanName ||
        form.changeSubscriptionPlanV2?.subscriptionId,
      );

      const days = String(form.changeSubscriptionPlanV2?.days || "").trim();

      if (days && (!/^\d+$/.test(days) || Number(days) <= 0)) {
        toast.error("Extend Days must be a positive number");
        return false;
      }

      // New subscription: user must either select a plan OR enter days.
      if (!hasExistingSubscription && !selectedSubscriptionPlanId && !days) {
        toast.error("Please select a subscription plan or enter days");
        return false;
      }
    }

    return true;
  };

  const getNormalizedSubmitForm = () => {
    const section = currentSection.section;
    const nextForm = {
      ...form,
      [section]: { ...form[section] },
    };

    if (section === "editOrganizationInfo") {
      const gstStatus = normalizeYesNo(nextForm[section].status, "N");

      nextForm[section] = {
        organizationName: normalizeValue(nextForm[section].organizationName),
        gst: gstStatus === "Y" ? normalizeValue(nextForm[section].gst) : "",
        pan_no: normalizeValue(nextForm[section].pan_no),
        brand_image: normalizeValue(nextForm[section].brand_image),
        pament_gateway: normalizeValue(
          nextForm[section].pament_gateway,
          "EASEBUZZ",
        ),
        taxGst:
          gstStatus === "Y" ? normalizeValue(nextForm[section].taxGst) : "",
        organizationType: normalizeOrganizationType(
          nextForm[section].organizationType,
          "1",
        ),
        gstType:
          gstStatus === "Y" ? normalizeValue(nextForm[section].gstType) : "",
        status: gstStatus,
      };
    }

    if (section === "updateMobileWhatsApp") {
      nextForm[section].phone_no = normalizeIndianMobileNumber(
        nextForm[section].phone_no,
      );
    }

    if (section === "insertRestaurantOrderDetails") {
      nextForm[section].takeaway = normalizeYesNo(
        nextForm[section].takeaway,
        "Y",
      );
      nextForm[section].homeDelivery = normalizeYesNo(
        nextForm[section].homeDelivery,
        "Y",
      );
      nextForm[section].dining = normalizeYesNo(nextForm[section].dining, "Y");
      nextForm[section].carOrder = normalizeYesNo(
        nextForm[section].carOrder,
        "Y",
      );
    }

    if (section === "createOrUpdateSubmerchant") {
      const submerchantStatus = normalizeYesNo(nextForm[section].status, "N");

      nextForm[section] = {
        status: submerchantStatus,
        submerchant_id: normalizeValue(
          nextForm[section].submerchant_id,
          submerchantStatus === "Y" ? DEFAULT_SUBMERCHANT_ID : "",
        ).trim(),
      };
    }

    if (section === "updateDeliveryCharges") {
      nextForm[section].chargeType = normalizeChargeType(
        nextForm[section].chargeType,
      );
    }

    if (section === "updateOrderTypeOptions") {
      nextForm[section].orderType = normalizeOrderTypeList(
        nextForm[section].orderType,
        "7",
      );
    }

    if (section === "insertOrderChargesConfig") {
      const activeOrderType =
        form.insertOrderChargesConfig?.activeOrderType || "";

      const activeConfig = (form.insertOrderChargesConfig?.configs || []).find(
        (item) => item.orderType === activeOrderType,
      );

      nextForm[section] = {
        fromAmount: normalizeValue(activeConfig?.fromAmount),
        toAmount: normalizeValue(activeConfig?.toAmount),
        charges: normalizeValue(activeConfig?.charges),
        chargesType: normalizeChargeType(activeConfig?.chargesType),
        chargesOn: normalizeValue(activeConfig?.chargesOn, "order"),

        // Step 8 API still expects home_delivery / car_order style values.
        orderType: normalizeSingleOrderType(activeOrderType, "7"),
      };
    }

    if (section === "changeSubscriptionPlanV2") {
      const days = String(form.changeSubscriptionPlanV2?.days || "").trim();
      const planId = String(selectedSubscriptionPlanId || "").trim();

      nextForm[section] = {
        userid: selectedClientCode,
        // Keep exact API schema: both fields are sent,
        // but only one contains a value.
        planId: days ? "" : planId,
        days: days || "",
      };
    }

    return nextForm;
  };

  const saveStep7OrderTypes = async () => {
    const selectedOrderTypes = normalizeOrderTypeList(
      form.updateOrderTypeOptions?.orderType,
      "7",
    );

    if (!selectedOrderTypes.length) {
      toast.error("Please select at least one order type");
      return {
        success: false,
      };
    }

    // v5.0 Step 7 expects orderType as an ARRAY.
    // Example: { orderType: ["home_delivery", "takeaway"], status: "Y" }
    const result = await onboardRestaurantStep({
      stepKey: "updateOrderTypeOptions",
      apiStep: "7",
      userid: selectedClientCode,
      adminId: getAdminPkId(),

      payload: {
        updateOrderTypeOptions: {
          orderType: selectedOrderTypes,
          status: "Y",
        },
      },

      showToast: false,
    });

    if (!result?.success) {
      toast.error("Failed to save Step 7");
      return result;
    }

    toast.success("Step 7 saved");

    return result;
  };

  const saveSubscriptionPlanStep = async () => {
    const days = String(form.changeSubscriptionPlanV2?.days || "").trim();
    const planId = String(selectedSubscriptionPlanId || "").trim();

    // Days mode is completed by the final v5.0 Step 9 API on Done.
    // Do not call the old changeSubscriptionPlan API for this mode.
    if (days) {
      if (!/^\d+$/.test(days) || Number(days) <= 0) {
        toast.error("Extend Days must be a positive number");
        return { success: false };
      }

      return { success: true, skipped: true, mode: "days" };
    }

    // View-only finish: existing subscription hai aur user ne plan change nahi kiya.
    if (
      !planId &&
      (currentSubscriptionPlan ||
        form.changeSubscriptionPlanV2?.currentPlanName ||
        form.changeSubscriptionPlanV2?.subscriptionId)
    ) {
      return { success: true, skipped: true };
    }

    if (!planId) {
      toast.error("Please select a subscription plan");
      return { success: false };
    }

    if (!changeSubscriptionPlan) {
      toast.error("changeSubscriptionPlan API function missing");
      return { success: false };
    }

    const currentPlanId = getCurrentSubscriptionPlanId();
    const currentPlanName = String(
      currentSubscriptionPlan?.plan_name ||
        form.changeSubscriptionPlanV2?.currentPlanName ||
        "",
    )
      .trim()
      .toLowerCase();
    const selectedPlan = SUBSCRIPTION_PLANS.find(
      (plan) => String(plan.pk_plan_id) === planId,
    );

    if (
      (currentPlanId && currentPlanId === planId) ||
      (!currentPlanId &&
        selectedPlan &&
        currentPlanName &&
        selectedPlan.plan_name.toLowerCase() === currentPlanName)
    ) {
      toast.success("This plan is already active");
      return { success: true, skipped: true };
    }

    try {
      setSubscriptionChanging(true);

      const result = await changeSubscriptionPlan({
        userid: selectedClientCode,
        planId,
      });

      if (!result?.success) {
        toast.error(result?.message || "Plan update failed");
        return result || { success: false };
      }

      toast.success(result?.message || "Subscription plan updated");
      await loadCurrentSubscription(selectedClientCode);
      return result;
    } catch (error) {
      console.error("changeSubscriptionPlan error:", error);
      toast.error("Change subscription API failed");
      return { success: false };
    } finally {
      setSubscriptionChanging(false);
    }
  };

  const handleNextStep = async () => {
    if (saving || uploadingImage || checkingRestaurant) return;
    if (!validateCurrentStep()) return;

    if (
      currentSection.section !== "changeSubscriptionPlanV2" &&
      !onboardRestaurantStep
    ) {
      toast.error("Step API function missing in useClients");
      return;
    }

    try {
      setSaving(true);
      let result;

      if (currentSection.section === "changeSubscriptionPlanV2") {
        result = await saveSubscriptionPlanStep();
      } else if (currentSection.section === "updateOrderTypeOptions") {
        result = await saveStep7OrderTypes();
      } else {
        result = await onboardRestaurantStep({
          stepKey: currentSection.section,
          apiStep: currentSection.apiStep,
          userid: selectedClientCode,
          adminId: getAdminPkId(),
          payload: getNormalizedSubmitForm(),
        });
      }

      if (!result?.success) return;

      setCompletedSteps((prev) => ({
        ...prev,
        [currentSection.section]: true,
      }));

      if (isLastStep) {
        const wasAlreadyOnboarded = isOnboarded;
        setIsOnboarded(true);
        toast.success(
          wasAlreadyOnboarded
            ? "Restaurant onboarding updated"
            : "Restaurant onboarding completed",
        );
        setFinishModalOpen(true);
        return;
      }

      setCurrentStepIndex((prev) => prev + 1);
    } catch (error) {
      console.error("step submit error:", error);
      toast.error("Step failed");
    } finally {
      setSaving(false);
    }
  };

  const resolveStep9PlanId = () => {
    const directPlanId = String(
      selectedSubscriptionPlanId ||
        getCurrentSubscriptionPlanId() ||
        form.changeSubscriptionPlanV2?.currentPlanId ||
        "",
    ).trim();

    if (directPlanId) return directPlanId;

    // getSubscriptionPlan may return only plan_name in some responses.
    // In that case resolve the plan id from the same plan list shown in Step 9.
    const currentPlanName = String(
      currentSubscriptionPlan?.plan_name ||
        form.changeSubscriptionPlanV2?.currentPlanName ||
        "",
    )
      .trim()
      .toLowerCase();

    if (!currentPlanName) return "";

    const countryCode = getSubscriptionCountry();
    const matchedPlan =
      SUBSCRIPTION_PLANS.find(
        (plan) =>
          String(plan.country_code || "").toUpperCase() === countryCode &&
          String(plan.plan_name || "")
            .trim()
            .toLowerCase() === currentPlanName,
      ) ||
      SUBSCRIPTION_PLANS.find(
        (plan) =>
          String(plan.plan_name || "")
            .trim()
            .toLowerCase() === currentPlanName,
      );

    return matchedPlan ? String(matchedPlan.pk_plan_id) : "";
  };

  const handleDoneOnboarding = async () => {
    if (finalizingOnboarding) return;

    if (!selectedClientCode) {
      toast.error("Selected restaurant is missing");
      return;
    }

    if (!onboardRestaurantStep) {
      toast.error("Step API function missing in useClients");
      return;
    }

    const days = String(form.changeSubscriptionPlanV2?.days || "").trim();
    const planId = days ? "" : resolveStep9PlanId();

    if (days && (!/^\d+$/.test(days) || Number(days) <= 0)) {
      toast.error("Extend Days must be a positive number");
      return;
    }

    if (!days && !planId) {
      toast.error("Please select a plan or enter extend days");
      return;
    }

    try {
      setFinalizingOnboarding(true);

      // IMPORTANT:
      // Step 9 must also be saved through /Admin/v5.0/onboardRestaurant.
      // changeSubscriptionPlan is only used to actually change the plan.
      // This call marks the restaurant onboarding flow as Step 9 completed.
      const result = await onboardRestaurantStep({
        stepKey: "changeSubscriptionPlanV2",
        apiStep: "9",
        userid: selectedClientCode,
        adminId: getAdminPkId(),
        payload: {
          changeSubscriptionPlanV2: {
            userid: selectedClientCode,
            // Exact v5 schema: always send both keys.
            // Only one will have a value at a time.
            planId: days ? "" : planId,
            days: days || "",
          },
        },
        showToast: false,
      });

      if (!result?.success) {
        toast.error(
          result?.data?.responseMessage ||
            result?.data?.message ||
            "Failed to complete onboarding Step 9",
        );
        return;
      }

      toast.success(
        result?.data?.responseMessage ||
          "Restaurant onboarding completed successfully",
      );

      handleReset();
    } catch (error) {
      console.error("Step 9 final onboarding error:", error);
      toast.error("Failed to complete onboarding Step 9");
    } finally {
      setFinalizingOnboarding(false);
    }
  };

  const handleBackStep = () => {
    if (isFirstStep || saving || uploadingImage || checkingRestaurant) return;
    setCurrentStepIndex((prev) => prev - 1);
  };

  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b pb-3">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Store size={17} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Onboarding Restaurant
            </h2>
            <p className="text-xs text-gray-500">
              Select a restaurant to create or edit onboarding configuration.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleReset}
          disabled={
            saving ||
            uploadingImage ||
            checkingRestaurant ||
            subscriptionChanging
          }
          className="h-9 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
        >
          Reset
        </button>
      </div>

      <div className="mb-4 grid gap-3 rounded-2xl border bg-gray-50/60 p-4 lg:grid-cols-[minmax(280px,1fr)_auto]">
        <label className="block text-sm">
          <div className="mb-1 text-xs font-medium text-gray-500">
            Select User / Restaurant
          </div>
          <ClientSearchSelect
            clients={clients}
            value={selectedClientCode}
            onChange={handleOrganizationSelect}
            placeholder="Select organization..."
            searchPlaceholder="Search by organization name or code..."
            emptyText="No organization found"
            noOptionsText="No clients available"
            className="!w-full"
          />
        </label>

        <div className="flex items-end">
          <div
            className={`inline-flex h-10 min-w-[170px] items-center justify-center gap-2 rounded-xl border px-4 text-sm font-semibold ${
              checkingRestaurant
                ? "bg-white text-gray-500"
                : isOnboarded
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : selectedClientCode
                    ? "border-blue-200 bg-blue-50 text-blue-700"
                    : "bg-white text-gray-400"
            }`}
          >
            {checkingRestaurant ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Checking...
              </>
            ) : isOnboarded ? (
              <>
                <BadgeCheck size={15} />
                Update Existing
              </>
            ) : selectedClientCode ? (
              <>
                <Store size={15} />
                Add New
              </>
            ) : (
              "Select restaurant"
            )}
          </div>
        </div>
      </div>

      {selectedClientCode ? (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {isOnboarded
                  ? "Update Restaurant Onboarding"
                  : "Create Restaurant Onboarding"}
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                {getClientName(selectedClient) || "Selected restaurant"} ·{" "}
                {selectedClientCode}
              </p>
            </div>

            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                isOnboarded
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-blue-50 text-blue-700"
              }`}
            >
              {isOnboarded ? "GET data loaded" : "New onboarding"}
            </span>
          </div>

          <div className="overflow-x-auto p-4">
            <div className="flex min-w-max items-center gap-2">
              {onboardingSections.map((section, index) => {
                const active = index === currentStepIndex;
                const done = completedSteps[section.section];

                return (
                  <button
                    key={section.section}
                    type="button"
                    disabled={saving || uploadingImage || checkingRestaurant}
                    onClick={() => {
                      setCurrentStepIndex(index);
                      if (section.section === "changeSubscriptionPlanV2") {
                        loadCurrentSubscription(selectedClientCode);
                      }
                    }}
                    className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                      active
                        ? "border-[#01C37D] bg-[#01C37D] text-white"
                        : done
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <span
                      className={`grid h-5 w-5 place-items-center rounded-full text-[11px] ${
                        active
                          ? "bg-white text-[#01C37D]"
                          : done
                            ? "bg-emerald-600 text-white"
                            : "bg-white text-gray-500"
                      }`}
                    >
                      {done ? <CheckIcon /> : index + 1}
                    </span>
                    {section.title}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mx-4 rounded-2xl border bg-gray-50/60 p-5">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-800">
                  Step {currentStepIndex + 1}: {currentSection.title}
                </div>
                <div className="text-xs text-gray-500">
                  {isOnboarded
                    ? "Existing values are editable."
                    : "Fill this section and save."}
                </div>
              </div>

              {completedSteps[currentSection.section] ? (
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  Saved
                </span>
              ) : null}
            </div>

            {currentSection.section === "changeSubscriptionPlanV2" ? (
              <div className="mb-5 space-y-4">
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        Current Subscription
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        Loaded from getSubscriptionPlan
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                        {form.changeSubscriptionPlanV2.currentPlanName ||
                          "No active plan"}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          loadCurrentSubscription(selectedClientCode)
                        }
                        disabled={subscriptionLoading || subscriptionChanging}
                        className="inline-flex h-8 items-center gap-1.5 rounded-lg border bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <RefreshCw
                          size={13}
                          className={subscriptionLoading ? "animate-spin" : ""}
                        />
                        Refresh
                      </button>
                    </div>
                  </div>

                  {subscriptionLoading ? (
                    <div className="flex min-h-32 items-center justify-center gap-2 text-sm text-slate-500">
                      <Loader2 size={17} className="animate-spin" />
                      Loading subscription...
                    </div>
                  ) : (
                    <div className="grid gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
                      <SubscriptionInfoItem
                        label="Subscription ID"
                        value={form.changeSubscriptionPlanV2.subscriptionId}
                      />
                      <SubscriptionInfoItem
                        label="Plan Name"
                        value={form.changeSubscriptionPlanV2.currentPlanName}
                      />
                      <SubscriptionInfoItem
                        label="Remaining Days"
                        value={
                          form.changeSubscriptionPlanV2.currentRemainingDays
                        }
                        highlight
                      />
                      <SubscriptionInfoItem
                        label="Billing Cycle"
                        value={form.changeSubscriptionPlanV2.billingCycle}
                      />
                      <SubscriptionInfoItem
                        label="Start Date"
                        value={formatSubscriptionDate(
                          form.changeSubscriptionPlanV2.startDate,
                        )}
                      />
                      <SubscriptionInfoItem
                        label="End Date"
                        value={formatSubscriptionDate(
                          form.changeSubscriptionPlanV2.endDate,
                        )}
                      />
                      <SubscriptionInfoItem
                        label="Purchased On"
                        value={formatSubscriptionDate(
                          form.changeSubscriptionPlanV2.purchasedOn,
                        )}
                      />
                      <SubscriptionInfoItem
                        label="Expired On"
                        value={formatSubscriptionDate(
                          form.changeSubscriptionPlanV2.expiredOn,
                        )}
                      />
                      <SubscriptionInfoItem
                        label="Subscription Type"
                        value={form.changeSubscriptionPlanV2.subscriptionType}
                      />
                      <SubscriptionInfoItem
                        label="Trial"
                        value={
                          String(form.changeSubscriptionPlanV2.isTrial) === "1"
                            ? "Yes"
                            : String(form.changeSubscriptionPlanV2.isTrial) ===
                                "0"
                              ? "No"
                              : form.changeSubscriptionPlanV2.isTrial
                        }
                      />
                      <SubscriptionInfoItem
                        label="Remark"
                        value={form.changeSubscriptionPlanV2.remark}
                      />
                      <SubscriptionInfoItem
                        label="Payment Status"
                        value={form.changeSubscriptionPlanV2.paymentStatus}
                      />
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border bg-white p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        Change Subscription Plan
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Select a plan. Finish Update will call
                        changeSubscriptionPlan with the selected plan ID.
                      </div>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                      {getSubscriptionCountry()} plans
                    </span>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {SUBSCRIPTION_PLANS.filter(
                      (plan) =>
                        Number(plan.is_active) === 1 &&
                        plan.country_code === getSubscriptionCountry(),
                    )
                      .sort((a, b) => a.sequence - b.sequence)
                      .map((plan) => {
                        const planId = String(plan.pk_plan_id);
                        const currentPlanId = getCurrentSubscriptionPlanId();
                        const currentPlanName = String(
                          form.changeSubscriptionPlanV2.currentPlanName || "",
                        ).toLowerCase();
                        const isCurrent =
                          (currentPlanId && currentPlanId === planId) ||
                          (!currentPlanId &&
                            currentPlanName === plan.plan_name.toLowerCase());
                        const isSelected =
                          selectedSubscriptionPlanId === planId;

                        return (
                          <button
                            key={plan.pk_plan_id}
                            type="button"
                            onClick={() => {
                              const nextPlanId = isCurrent ? "" : planId;
                              setSelectedSubscriptionPlanId(nextPlanId);

                              // planId and days are mutually exclusive.
                              if (nextPlanId) {
                                setForm((prev) => ({
                                  ...prev,
                                  changeSubscriptionPlanV2: {
                                    ...prev.changeSubscriptionPlanV2,
                                    days: "",
                                  },
                                }));
                              }
                            }}
                            disabled={subscriptionChanging}
                            className={`relative rounded-2xl border p-4 text-left transition disabled:opacity-60 ${
                              isSelected
                                ? "border-[#01C37D] bg-emerald-50 shadow-sm"
                                : isCurrent
                                  ? "border-emerald-200 bg-emerald-50/50"
                                  : "border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {plan.plan_name}
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                  {plan.plan_duration}
                                </div>
                              </div>
                              {isCurrent ? (
                                <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-bold text-emerald-700">
                                  CURRENT
                                </span>
                              ) : isSelected ? (
                                <span className="grid h-6 w-6 place-items-center rounded-full bg-[#01C37D] text-white">
                                  <CheckIcon />
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-4 text-lg font-bold text-gray-900">
                              {plan.currency} {plan.plan_price}
                            </div>
                            <div className="mt-1 text-[11px] text-gray-500">
                              Plan ID: {plan.pk_plan_id}
                            </div>
                          </button>
                        );
                      })}
                  </div>

                  {selectedSubscriptionPlanId ? (
                    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                      Selected plan:{" "}
                      {
                        SUBSCRIPTION_PLANS.find(
                          (plan) =>
                            String(plan.pk_plan_id) ===
                            selectedSubscriptionPlanId,
                        )?.plan_name
                      }
                    </div>
                  ) : null}

                  <div className="my-4 flex items-center gap-3">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      OR
                    </span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>

                  <div
                    className={`rounded-2xl border p-4 ${
                      form.changeSubscriptionPlanV2?.days
                        ? "border-[#01C37D] bg-emerald-50/60"
                        : "border-gray-200 bg-gray-50/60"
                    }`}
                  >
                    <div className="text-sm font-semibold text-gray-900">
                      Extend Subscription Days
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Enter days only when you want to extend the current
                      subscription. Selecting days will clear the selected Plan
                      ID.
                    </div>

                    <div className="mt-3 max-w-sm">
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        Days
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.changeSubscriptionPlanV2?.days || ""}
                        onChange={(event) => {
                          const days = event.target.value
                            .replace(/\D/g, "")
                            .slice(0, 5);

                          if (days) {
                            setSelectedSubscriptionPlanId("");
                          }

                          setForm((prev) => ({
                            ...prev,
                            changeSubscriptionPlanV2: {
                              ...prev.changeSubscriptionPlanV2,
                              days,
                            },
                          }));
                        }}
                        placeholder="e.g. 30"
                        disabled={subscriptionChanging}
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D] disabled:opacity-60"
                      />
                    </div>

                    {form.changeSubscriptionPlanV2?.days ? (
                      <div className="mt-3 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-xs font-medium text-emerald-800">
                        {form.changeSubscriptionPlanV2.days} day(s) will be sent
                        in Step 9.
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {currentSection.fields.map((field) => {
                const isStep8Section =
                  currentSection.section === "insertOrderChargesConfig";
                const step8State = form.insertOrderChargesConfig || {};
                const activeStep8OrderType = step8State.activeOrderType || "";
                const activeStep8Config = (
                  Array.isArray(step8State.configs) ? step8State.configs : []
                ).find((item) => item.orderType === activeStep8OrderType);

                const isStep8OrderTypeField = field.type === "orderTypeStep8";
                const isStep8ConfigField =
                  field.type === "step8ConfigField" ||
                  field.type === "step8ChargeTypeSelect" ||
                  field.type === "step8ChargesOnSelect";

                if (
                  isStep8Section &&
                  isStep8ConfigField &&
                  !activeStep8OrderType
                ) {
                  return null;
                }

                const fieldValue =
                  isStep8Section && isStep8ConfigField
                    ? (activeStep8Config?.[field.name] ?? "")
                    : (form[currentSection.section]?.[field.name] ?? "");

                const isSubmerchantSection =
                  currentSection.section === "createOrUpdateSubmerchant";

                const isSubmerchantEnabled =
                  normalizeYesNo(
                    form.createOrUpdateSubmerchant?.status,
                    "N",
                  ) === "Y";

                const isToggleEnabled = normalizeYesNo(fieldValue, "N") === "Y";
                const isOrderTypeField =
                  field.type === "orderTypeStep7" ||
                  field.type === "orderTypeStep8";

                const orderTypeOptions =
                  field.type === "orderTypeStep8"
                    ? STEP_8_ORDER_TYPE_OPTIONS
                    : STEP_7_ORDER_TYPE_OPTIONS;

                const selectedOrderTypes =
                  field.type === "orderTypeStep8"
                    ? activeStep8OrderType
                      ? [activeStep8OrderType]
                      : []
                    : isOrderTypeField
                      ? normalizeOrderTypeList(fieldValue, "7")
                      : [];

                const isGstEnabled =
                  normalizeYesNo(form.editOrganizationInfo?.status, "Y") ===
                  "Y";
                const isLockedGstField =
                  currentSection.section === "editOrganizationInfo" &&
                  ["gst", "taxGst", "gstType"].includes(field.name) &&
                  isGstEnabled;

                const isCompactToggle = field.type === "ynToggle";
                const isFullWidthField =
                  field.type === "ynToggleGroup" || isOrderTypeField;

                return (
                  <div
                    key={field.name}
                    className={`text-sm ${
                      isFullWidthField
                        ? "md:col-span-2 lg:col-span-3"
                        : isCompactToggle
                          ? "w-fit self-end"
                          : "block"
                    }`}
                  >
                    {!isCompactToggle && field.type !== "ynToggleGroup" ? (
                      <div className="mb-1 text-xs font-medium text-gray-500">
                        {field.label}
                        {field.required ? (
                          <span className="ml-1 text-rose-500">*</span>
                        ) : null}
                      </div>
                    ) : null}

                    {field.type === "clientSelect" ? (
                      <input
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D]"
                        value={form.editOrganizationInfo.organizationName}
                        onChange={(e) =>
                          updateField(
                            currentSection.section,
                            field.name,
                            e.target.value,
                          )
                        }
                      />
                    ) : field.type === "organizationTypeSelect" ? (
                      <select
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D]"
                        value={fieldValue || "1"}
                        onChange={(e) =>
                          updateField(
                            currentSection.section,
                            field.name,
                            e.target.value,
                          )
                        }
                      >
                        {ORGANIZATION_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : field.type === "gateway" ? (
                      <select
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D]"
                        value={fieldValue || "EASEBUZZ"}
                        onChange={(e) =>
                          updateField(
                            currentSection.section,
                            field.name,
                            e.target.value,
                          )
                        }
                      >
                        <option value="EASEBUZZ">EASEBUZZ</option>
                        {(gateways || []).map((gate, index) => {
                          if (!isGatewayActive(gate)) return null;

                          const gatewayName = String(
                            gate?.gateway_name || "",
                          ).trim();

                          if (
                            !gatewayName ||
                            gatewayName.toUpperCase() === "EASEBUZZ"
                          ) {
                            return null;
                          }

                          return (
                            <option
                              key={`${gatewayName}-${index}`}
                              value={gatewayName}
                            >
                              {gatewayName}
                            </option>
                          );
                        })}
                      </select>
                    ) : field.type === "ynToggleGroup" ? (
                      <div>
                        <div className="mb-2 text-xs font-medium text-gray-500">
                          {field.label}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {(field.options || []).map((option) => {
                            const enabled =
                              normalizeYesNo(
                                form[currentSection.section]?.[option.name],
                                "N",
                              ) === "Y";

                            return (
                              <div
                                key={option.name}
                                className="inline-flex h-10 items-center gap-3 rounded-xl border bg-white px-3"
                              >
                                <span className="whitespace-nowrap text-xs font-medium text-gray-700">
                                  {option.label}
                                </span>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-label={option.label}
                                  aria-checked={enabled}
                                  onClick={() =>
                                    updateField(
                                      currentSection.section,
                                      option.name,
                                      enabled ? "N" : "Y",
                                    )
                                  }
                                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                                    enabled ? "bg-[#01C37D]" : "bg-gray-300"
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                                      enabled
                                        ? "translate-x-[22px]"
                                        : "translate-x-0.5"
                                    }`}
                                  />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : field.type === "ynToggle" ? (
                      <div className="inline-flex h-10 items-center gap-3 rounded-xl border bg-white px-3">
                        <span className="whitespace-nowrap text-xs font-medium text-gray-700">
                          {field.label}
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-label={field.label}
                          aria-checked={isToggleEnabled}
                          onClick={() =>
                            updateField(
                              currentSection.section,
                              field.name,
                              isToggleEnabled ? "N" : "Y",
                            )
                          }
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                            isToggleEnabled ? "bg-[#01C37D]" : "bg-gray-300"
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                              isToggleEnabled
                                ? "translate-x-[22px]"
                                : "translate-x-0.5"
                            }`}
                          />
                        </button>
                      </div>
                    ) : field.type === "watermarkSelect" ? (
                      <select
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D]"
                        value={fieldValue}
                        onChange={(e) =>
                          updateField(
                            currentSection.section,
                            field.name,
                            e.target.value,
                          )
                        }
                      >
                        <option value="Order Managed By Chaafo.">
                          Order Managed By Chaafo
                        </option>
                        <option value="Order Managed By Anlook.">
                          Order Managed By Anlook
                        </option>
                      </select>
                    ) : field.type === "gstTypeSelect" ? (
                      <select
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                        value={fieldValue}
                        disabled={isLockedGstField}
                        onChange={(e) =>
                          updateField(
                            currentSection.section,
                            field.name,
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Select GST Type</option>
                        <option value="excl">Exclusive</option>
                        <option value="incl">Inclusive</option>
                      </select>
                    ) : field.type === "chargeTypeSelect" ? (
                      <select
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D]"
                        value={fieldValue}
                        onChange={(e) =>
                          updateField(
                            currentSection.section,
                            field.name,
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Select</option>
                        <option value="fixed">Fixed</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    ) : field.type === "step8ChargeTypeSelect" ? (
                      <select
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D]"
                        value={fieldValue}
                        onChange={(e) =>
                          updateStep8ConfigField(field.name, e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        <option value="fixed">Fixed</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    ) : field.type === "step8ChargesOnSelect" ? (
                      <select
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D]"
                        value={fieldValue}
                        onChange={(e) =>
                          updateStep8ConfigField(field.name, e.target.value)
                        }
                      >
                        <option value="message">Per Message</option>
                        <option value="order">Per Order</option>
                      </select>
                    ) : isOrderTypeField ? (
                      /* CLEAN SELECTABLE BUTTONS / PILLS FOR STEP 7 & 8 */
                      <div className="flex flex-wrap gap-2 rounded-xl border bg-white p-3">
                        {orderTypeOptions.map((option) => {
                          const checked = selectedOrderTypes.includes(
                            option.value,
                          );
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                if (isStep8OrderTypeField) {
                                  selectStep8OrderType(option.value);
                                } else {
                                  toggleOrderType(
                                    currentSection.section,
                                    field.name,
                                    option.value,
                                    "7",
                                  );
                                }
                              }}
                              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-xs font-semibold transition ${
                                checked
                                  ? "border-[#01C37D] bg-[#01C37D] text-white shadow-sm"
                                  : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              <span>{option.label}</span>
                              {checked && <CheckIcon />}
                            </button>
                          );
                        })}
                      </div>
                    ) : field.type === "image" ? (
                      <div className="space-y-2">
                        <label className="flex h-28 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed bg-white px-3 text-center hover:bg-gray-50">
                          {imagePreview ||
                          form.editOrganizationInfo.brand_image ? (
                            <img
                              src={
                                imagePreview ||
                                form.editOrganizationInfo.brand_image
                              }
                              alt="Brand Preview"
                              className="h-full max-h-24 w-full rounded-lg object-contain"
                            />
                          ) : (
                            <div>
                              <div className="text-xs font-semibold text-gray-700">
                                Upload Brand Image
                              </div>
                              <div className="mt-1 text-[11px] text-gray-400">
                                JPG / PNG / WEBP under 2MB
                              </div>
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={uploadingImage}
                            onChange={handleBrandImageSelect}
                          />
                        </label>
                        {uploadingImage && (
                          <div className="text-xs text-amber-600">
                            Uploading image...
                          </div>
                        )}
                      </div>
                    ) : (
                      <input
                        className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none focus:border-[#01C37D] read-only:cursor-not-allowed read-only:bg-gray-100 read-only:text-gray-500"
                        value={fieldValue}
                        readOnly={isLockedGstField}
                        inputMode={
                          (currentSection.section === "updateMobileWhatsApp" &&
                            field.name === "phone_no") ||
                          (currentSection.section ===
                            "changeSubscriptionPlanV2" &&
                            field.name === "days")
                            ? "numeric"
                            : undefined
                        }
                        maxLength={
                          currentSection.section === "updateMobileWhatsApp" &&
                          field.name === "phone_no"
                            ? 10
                            : undefined
                        }
                        onChange={(e) => {
                          let value = e.target.value;
                          if (
                            currentSection.section === "updateMobileWhatsApp" &&
                            field.name === "phone_no"
                          ) {
                            value = value.replace(/\D/g, "").slice(0, 10);
                          }

                          if (
                            currentSection.section ===
                              "changeSubscriptionPlanV2" &&
                            field.name === "days"
                          ) {
                            value = value.replace(/\D/g, "");
                          }

                          if (isStep8Section && isStep8ConfigField) {
                            updateStep8ConfigField(field.name, value);
                          } else {
                            updateField(
                              currentSection.section,
                              field.name,
                              value,
                            );
                          }
                        }}
                        placeholder={field.label}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mx-4 mt-4 flex items-center justify-between gap-3 border-t p-4">
            <button
              type="button"
              onClick={handleBackStep}
              disabled={
                isFirstStep || saving || uploadingImage || checkingRestaurant
              }
              className="h-9 rounded-xl border px-4 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Back
            </button>

            <div className="text-xs text-gray-500">
              Step {currentStepIndex + 1} of {onboardingSections.length}
            </div>

            <button
              type="button"
              onClick={handleNextStep}
              disabled={
                saving ||
                uploadingImage ||
                checkingRestaurant ||
                subscriptionChanging
              }
              className="inline-flex h-9 items-center gap-2 rounded-xl bg-[#01C37D] px-5 text-sm font-semibold text-white hover:bg-[#01C37D]/90 disabled:opacity-60"
            >
              {saving || uploadingImage || subscriptionChanging ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Save size={15} />
              )}
              {subscriptionChanging
                ? "Updating Plan..."
                : saving
                  ? "Saving..."
                  : uploadingImage
                    ? "Uploading..."
                    : isLastStep
                      ? isOnboarded
                        ? "Finish Update"
                        : "Finish"
                      : "Save & Next"}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid place-items-center rounded-2xl border border-dashed bg-gray-50 py-12 text-sm text-gray-500">
          Select a user / restaurant to start onboarding.
        </div>
      )}

      {/* FINISH & SUMMARY MODAL */}
      {finishModalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="border-b px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-emerald-50 text-emerald-600">
                  <BadgeCheck size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Restaurant onboarding completed
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Configuration loaded for{" "}
                    {getClientName(selectedClient) || selectedClientCode}.
                  </p>
                </div>
                <span className="ml-auto rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  Step {finishResponseRow?.step || 9} Completed
                </span>
              </div>
            </div>

            <div className="overflow-y-auto p-5">
              <div className="grid gap-4 md:grid-cols-2">
                <SummaryStep
                  title="Step 1 · Organization Info"
                  items={[
                    [
                      "Organization Name",
                      form.editOrganizationInfo.organizationName,
                    ],
                    [
                      "GST Status",
                      form.editOrganizationInfo.status === "Y"
                        ? "Enabled"
                        : "Disabled",
                    ],
                    ["GST", form.editOrganizationInfo.gst],
                    ["PAN No", form.editOrganizationInfo.pan_no],
                    [
                      "Organization Type",
                      ORGANIZATION_TYPE_OPTIONS.find(
                        (item) =>
                          item.value ===
                          form.editOrganizationInfo.organizationType,
                      )?.label || "Restaurant",
                    ],
                    [
                      "Payment Gateway",
                      form.editOrganizationInfo.pament_gateway,
                    ],
                    ["Tax GST", form.editOrganizationInfo.taxGst],
                    ["GST Type", form.editOrganizationInfo.gstType],
                  ]}
                />

                <SummaryStep
                  title="Step 2 · Receipt Data"
                  items={[
                    ["Address", form.editReceiptData.address],
                    ["Phone No", form.editReceiptData.phone_no],
                    ["Tag Line 1", form.editReceiptData.tagLine1],
                    ["Tag Line 2", form.editReceiptData.tagLine2],
                    ["Watermark 1", form.editReceiptData.watermark1],
                    ["Watermark 2", form.editReceiptData.watermark2],
                    ["GST", form.editReceiptData.gst],
                  ]}
                />

                <SummaryStep
                  title="Step 3 · Mobile / WhatsApp"
                  items={[
                    ["WhatsApp Phone No", form.updateMobileWhatsApp.phone_no],
                  ]}
                />

                <SummaryStep
                  title="Step 4 · Restaurant Order Details"
                  items={[
                    [
                      "Restaurant GST",
                      form.insertRestaurantOrderDetails.restaurantgst,
                    ],
                    [
                      "Restaurant Name",
                      form.insertRestaurantOrderDetails.restaurantName,
                    ],
                    [
                      "Address",
                      form.insertRestaurantOrderDetails.restaurantAddress,
                    ],
                    [
                      "Pincode",
                      form.insertRestaurantOrderDetails.restaurantPincode,
                    ],
                    [
                      "Home Delivery",
                      form.insertRestaurantOrderDetails.homeDelivery,
                    ],
                    ["Takeaway", form.insertRestaurantOrderDetails.takeaway],
                    ["Dining", form.insertRestaurantOrderDetails.dining],
                    ["Car Order", form.insertRestaurantOrderDetails.carOrder],
                  ]}
                />

                <SummaryStep
                  title="Step 5 · Submerchant"
                  items={[
                    ["Status", form.createOrUpdateSubmerchant.status],
                    [
                      "Submerchant ID",
                      form.createOrUpdateSubmerchant.submerchant_id,
                    ],
                  ]}
                />

                <SummaryStep
                  title="Step 6 · Delivery Charges"
                  items={[
                    ["Charges", form.updateDeliveryCharges.charges],
                    ["From Amount", form.updateDeliveryCharges.fromAmount],
                    ["To Amount", form.updateDeliveryCharges.toAmount],
                    ["Charge Type", form.updateDeliveryCharges.chargeType],
                  ]}
                />

                <SummaryStep
                  title="Step 7 · POS Order Type Options"
                  items={(form.updateOrderTypeOptions.orderType || []).map(
                    (type) => [formatOrderType(type), "Y"],
                  )}
                />

                <div className="rounded-2xl border bg-gray-50/60 p-4 md:col-span-2">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Step 8 · Order Charges Config
                    </h4>
                    <span className="text-xs text-gray-500">
                      {form.insertOrderChargesConfig.configs?.length || 0}{" "}
                      configuration(s)
                    </span>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                    {(form.insertOrderChargesConfig.configs || []).map(
                      (config) => (
                        <div
                          key={`${config.orderType}-${config.pkChargeId || "new"}`}
                          className="rounded-xl border bg-white p-3"
                        >
                          <div className="mb-2 text-sm font-semibold text-[#01C37D]">
                            {formatOrderType(config.orderType)}
                          </div>
                          <SummaryValue
                            label="From Amount"
                            value={config.fromAmount}
                          />
                          <SummaryValue
                            label="To Amount"
                            value={config.toAmount}
                          />
                          <SummaryValue
                            label="Charges"
                            value={config.charges}
                          />
                          <SummaryValue
                            label="Charges Type"
                            value={config.chargesType}
                          />
                          <SummaryValue
                            label="Charges On"
                            value={
                              config.chargesOn === "message"
                                ? "Per Message"
                                : config.chargesOn === "order"
                                  ? "Per Order"
                                  : config.chargesOn
                            }
                          />
                        </div>
                      ),
                    )}
                  </div>
                </div>

                <SummaryStep
                  title="Step 9 · Subscription Plan"
                  items={[
                    ["User ID", selectedClientCode],
                    [
                      "Subscription ID",
                      form.changeSubscriptionPlanV2.subscriptionId,
                    ],
                    [
                      "Current Plan",
                      form.changeSubscriptionPlanV2.currentPlanName,
                    ],
                    [
                      "Current Remaining Days",
                      form.changeSubscriptionPlanV2.currentRemainingDays,
                    ],
                    [
                      "Billing Cycle",
                      form.changeSubscriptionPlanV2.billingCycle,
                    ],
                    [
                      "Start Date",
                      formatSubscriptionDate(
                        form.changeSubscriptionPlanV2.startDate,
                      ),
                    ],
                    [
                      "End Date",
                      formatSubscriptionDate(
                        form.changeSubscriptionPlanV2.endDate,
                      ),
                    ],
                    [
                      "Purchased On",
                      formatSubscriptionDate(
                        form.changeSubscriptionPlanV2.purchasedOn,
                      ),
                    ],
                    [
                      "Expired On",
                      formatSubscriptionDate(
                        form.changeSubscriptionPlanV2.expiredOn,
                      ),
                    ],
                    [
                      "Subscription Type",
                      form.changeSubscriptionPlanV2.subscriptionType,
                    ],
                    [
                      "Trial",
                      String(form.changeSubscriptionPlanV2.isTrial) === "1"
                        ? "Yes"
                        : String(form.changeSubscriptionPlanV2.isTrial) === "0"
                          ? "No"
                          : form.changeSubscriptionPlanV2.isTrial,
                    ],
                    ["Remark", form.changeSubscriptionPlanV2.remark],
                    [
                      "Payment Status",
                      form.changeSubscriptionPlanV2.paymentStatus,
                    ],
                    ["Plan ID", form.changeSubscriptionPlanV2.currentPlanId],
                    ["Extend Days", form.changeSubscriptionPlanV2.days],
                    [
                      "Plan Price",
                      form.changeSubscriptionPlanV2.currentPlanPrice
                        ? `${form.changeSubscriptionPlanV2.currentPlanCurrency || ""} ${form.changeSubscriptionPlanV2.currentPlanPrice}`.trim()
                        : "",
                    ],
                  ]}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 border-t p-4">
              <button
                type="button"
                onClick={() => setShowWhatsappQrModal(true)}
                disabled={finalizingOnboarding}
                className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-500 bg-emerald-50 text-sm font-semibold text-[#01C37D] hover:bg-emerald-100"
              >
                <QrCode size={18} />
                Generate WhatsApp QR
              </button>

              <button
                type="button"
                onClick={handleDoneOnboarding}
                disabled={finalizingOnboarding}
                className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-[#01C37D] text-sm font-semibold text-white hover:bg-[#01C37D]/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {finalizingOnboarding ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Completing...
                  </>
                ) : (
                  "Done"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <WhatsappQrGenerateModal
        open={showWhatsappQrModal}
        onClose={() => setShowWhatsappQrModal(false)}
        clientId={selectedClientCode}
        clientName={getClientName(selectedClient)}
        defaultLoginNumber={
          form.updateMobileWhatsApp.phone_no ||
          selectedClient?.us_loginid ||
          selectedClient?.mobile ||
          ""
        }
      />
    </div>
  );
}

function formatSubscriptionDate(value) {
  if (value === null || value === undefined || value === "") return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function SubscriptionInfoItem({ label, value, highlight = false }) {
  const displayValue =
    value === null || value === undefined || value === "" ? "—" : String(value);

  return (
    <div className="min-h-[78px] bg-white px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div
        className={`mt-1.5 break-words text-sm font-semibold ${
          highlight ? "text-[#01C37D]" : "text-slate-800"
        }`}
      >
        {displayValue}
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function SummaryStep({ title, items = [] }) {
  return (
    <div className="rounded-2xl border bg-gray-50/60 p-4">
      <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
      <div className="space-y-2">
        {items.map(([label, value]) => (
          <SummaryValue key={label} label={label} value={value} />
        ))}
      </div>
    </div>
  );
}

function SummaryValue({ label, value }) {
  const displayValue =
    value === null || value === undefined || value === "" ? "—" : String(value);

  return (
    <div className="flex items-start justify-between gap-4 text-xs">
      <span className="shrink-0 text-gray-500">{label}</span>
      <span className="break-all text-right font-medium text-gray-800">
        {displayValue}
      </span>
    </div>
  );
}
